import { Box, Stack, TextField, Typography } from '@mui/material';
import { type SxProps, type Theme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { isValidColor, normalizeHex } from 'src/utils/color-utils';

const COMMIT_DELAY_MS = 300;
const DEFAULT_HEX = '#000000';

const swatchRingSx: SxProps<Theme> = {
  position: 'relative',
  flexShrink: 0,
  borderRadius: '50%',
  overflow: 'hidden',
  boxShadow: (theme) =>
    `0 0 0 1px ${theme.palette.divider}, 0 1px 2px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.08)'}`,
};

const swatchInputSx: SxProps<Theme> = {
  display: 'block',
  width: 28,
  height: 28,
  p: 0,
  border: 'none',
  backgroundColor: 'transparent',
  '&::-webkit-color-swatch-wrapper': { padding: 0 },
  '&::-webkit-color-swatch': { border: 'none', borderRadius: 4 },
  '&::-moz-color-swatch': { border: 'none', borderRadius: 4 },
};

interface ColorPickerFieldProps {
  field: { value: unknown; onChange: (value: string) => void };
  label: string;
  readOnly: boolean;
  hasError: boolean;
}

// The native picker fires `change` throughout a drag, so commits to the form are debounced.
export const ColorPickerField = ({
  field,
  label,
  readOnly,
  hasError,
}: ColorPickerFieldProps) => {
  const rawValue = (field.value as string | undefined) ?? '';
  const formHex = normalizeHex(rawValue) || DEFAULT_HEX;
  const [localHex, setLocalHex] = useState(rawValue.trim() || formHex);
  const commitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingCommit = () => {
    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current);
      commitTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    setLocalHex(rawValue.trim() || formHex);
  }, [rawValue, formHex]);

  useEffect(() => clearPendingCommit, []);

  const commitToForm = (hex: string) => {
    clearPendingCommit();
    field.onChange(normalizeHex(hex) || (isValidColor(formHex) ? formHex : DEFAULT_HEX));
  };

  const handlePickerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = normalizeHex(event.target.value) || DEFAULT_HEX;
    setLocalHex(next);
    clearPendingCommit();
    commitTimeoutRef.current = setTimeout(() => commitToForm(next), COMMIT_DELAY_MS);
  };

  const handlePickerBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const raw = (event.target.value || '').trim();
    const next = isValidColor(raw) ? normalizeHex(raw) || raw : DEFAULT_HEX;
    clearPendingCommit();
    setLocalHex(next);
    field.onChange(next);
  };

  const handleHexBlur = () => {
    const trimmed = rawValue.trim();
    if (trimmed && !isValidColor(trimmed)) {
      field.onChange(DEFAULT_HEX);
      setLocalHex(DEFAULT_HEX);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      py={1.5}
      flexWrap="wrap"
    >
      <Stack direction="row" alignItems="center" gap={1.5} minWidth={0} flex={1}>
        <Box component="span" sx={swatchRingSx}>
          <Box
            component="input"
            type="color"
            value={isValidColor(localHex) ? localHex : DEFAULT_HEX}
            disabled={readOnly}
            draggable={false}
            aria-label={`${label} color picker`}
            onChange={handlePickerChange}
            onBlur={handlePickerBlur}
            onMouseUp={() => commitToForm(localHex)}
            onPointerUp={() => commitToForm(localHex)}
            sx={{ ...swatchInputSx, cursor: readOnly ? 'default' : 'pointer' }}
          />
        </Box>
        <Typography variant="body2" fontWeight={500} color="text.primary">
          {label}
        </Typography>
      </Stack>
      <TextField
        size="small"
        variant="outlined"
        value={rawValue}
        disabled={readOnly}
        onChange={(event) => field.onChange(event.target.value)}
        onBlur={handleHexBlur}
        placeholder={DEFAULT_HEX}
        error={hasError}
        inputProps={{ 'aria-label': `${label} hex`, maxLength: 7 }}
        sx={{ width: 112, flexShrink: 0 }}
      />
    </Stack>
  );
};
